import { z } from "zod";
import { clientAccountStatusSchema, clientUpsertSchema } from "./clients";
import { localizedTextSchema, timestampsSchema, withoutDefaults } from "./common";
import { listQueryBaseSchema, parseUuidList, queryBoolean } from "./list-query";
import { mediaDtoSchema } from "./media";
import { reservationAddonInputSchema } from "./addons";
import { RESERVATION_STATUS } from "../constants/status";
import { RESERVATION_TAG_IDS } from "../constants/reservation-tags";
import { transferRecordSchema, transferRouteConfigSchema } from "../transfer";

export const reservationAddonDtoSchema = z.object({
  variantId: z.string().uuid(),
  quantity: z.number().int().positive(),
  unitPrice: z.number().nonnegative(),
  labelSnapshot: z.string(),
  addonNameSnapshot: z.string(),
});
export type ReservationAddonDTO = z.infer<typeof reservationAddonDtoSchema>;

const statusValues = Object.values(RESERVATION_STATUS) as [number, ...number[]];

const reservationStatusSchema = z.union([
  z.literal(statusValues[0]),
  ...statusValues.slice(1).map((v) => z.literal(v)),
]);

/**
 * One booked line: which activity, on what date, at what time, which package,
 * and how many of it. A spa booking has several of these.
 */
const reservationActivitySchema = z.object({
  id: z.string(),
  title: localizedTextSchema,
  slug: z.string(),
  city: z.object({ id: z.string(), name: localizedTextSchema }).nullable().optional(),
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  hideLocation: z.boolean().default(false),
  category: z
    .object({
      id: z.string(),
      name: localizedTextSchema,
      slug: z.string().nullable().optional(),
      template: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  heroMedia: mediaDtoSchema.nullable().optional(),
  /** Provider takes cash only on arrival (no card machine). */
  cashOnlyOnArrival: z.boolean().default(false),
  reservationDate: z.string().nullable(),
  /** Who is playing that night, for a venue with a lineup. */
  lineup: z.string().nullable().optional(),
  /** The transfer route's trip kind (`hourly` = driver by the day). Null off transfer routes. */
  transferTrip: transferRouteConfigSchema.shape.trip.nullable().optional(),
  reservationTime: z.string().nullable(),
  /** End of a flexible package's window ("15:00" for 13:00–15:00). Null = exact time. */
  reservationTimeEnd: z.string().nullable().optional(),
  package: z
    .object({ id: z.string(), name: localizedTextSchema })
    .nullable()
    .optional(),
  quantity: z.number().int().default(1),
  /** What this line adds to the booking total. */
  linePrice: z.number().default(0),
  /** Live packages on the listing; 1 means the guest picked nothing. */
  packageCount: z.number().int().optional(),
  /** Women-only group trip: the guest owes the organiser a deposit. */
  womenOnly: z.boolean().optional(),
  /** Deposit per seat, whole MAD. Null when the trip asks for none. */
  depositAmount: z.number().int().nullable().optional(),
});

/**
 * One change a guest made to their own booking. `field` is what moved;
 * `from` / `to` are display-ready ("2026-10-18", "2A / 0C / 0I", "800").
 */
export const guestChangeFieldSchema = z.enum(["date", "time", "guests", "transport", "total"]);
export type GuestChangeField = z.infer<typeof guestChangeFieldSchema>;
export const guestChangeEntrySchema = z.object({
  /** ISO timestamp of the edit. */
  at: z.string(),
  changes: z.array(
    z.object({ field: guestChangeFieldSchema, from: z.string(), to: z.string() }),
  ),
});
export type GuestChangeEntry = z.infer<typeof guestChangeEntrySchema>;

export const reservationDtoSchema = z
  .object({
    id: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    /** Whether the guest has a website/app account. Set on list rows only. */
    accountStatus: clientAccountStatusSchema.optional(),
    clientId: z.string().uuid().nullable(),
    client: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().optional(),
        whatsappNumber: z.string().optional(),
      })
      .nullable(),
    couponId: z.string().uuid().nullable(),
    comboId: z.string().uuid().nullable(),
    combo: z.object({ id: z.string(), name: localizedTextSchema }).nullable(),
    activity: reservationActivitySchema.nullable(),
    /**
     * Every line on the booking. A spa booking holds several treatments, each
     * with its own quantity; everywhere else this is one entry and `activity`
     * above is the same thing. Readers that only need "which activity" keep
     * using `activity`.
     */
    activities: z.array(reservationActivitySchema).default([]),
    reservationPrice: z.string(),
    commission: z.string(),
    status: reservationStatusSchema,
    rejectionReason: z.string().nullable(),
    confirmationReason: z.string().nullable(),
    cancelReason: z.string().nullable(),
    /** "client" | "team" — who cancelled it. Null on rows from before we recorded it. */
    cancelledBy: z.string().nullable(),
    adults: z.number().int(),
    children: z.number().int(),
    infants: z.number().int(),
    infantMaxAge: z.number().int().nullable(),
    childrenMaxAge: z.number().int().nullable(),
    transport: z.boolean(),
    transportPrice: z.string().nullable(),
    /** Pickup was from outside the activity's city (surcharge applied). */
    transportOutside: z.boolean().default(false),
    address: z.string().nullable(),
    transfer: transferRecordSchema.nullable().default(null),
    groups: z.number().int(),
    rooms: z.number().int(),
    hours: z.number().int(),
    nights: z.number().int(),
    lang: z.string(),
    paymentMethod: z.string().nullable(),
    source: z.string(),
    /** Google Ads click id, or null. Non-null == the booking came from an ad. */
    gclid: z.string().nullable(),
    /** How the visitor first reached us — instagram, tiktok, google_organic, … */
    trafficSource: z.string().nullable(),
    /** Which chat assistant, when trafficSource is ai_assistant (see AI_ASSISTANTS). */
    aiAssistant: z.string().nullable(),
    /** First-touch utm_campaign: the numeric Google Ads campaign id, or null. */
    adCampaign: z.string().nullable(),
    bookingId: z.string().nullable(),
    notes: z.string().nullable(),
    // Ops outreach tracking — ISO timestamp of when we contacted the client on
    // each channel, or null if not contacted yet. Drives the admin "Contacted"
    // indicators so the team can triage who still needs a follow-up.
    contactedWhatsappAt: z.string().nullable(),
    contactedEmailAt: z.string().nullable(),
    // Guest-side confirmation by email (guests not on WhatsApp): when we asked,
    // when the one reminder went out, and when the guest clicked Confirm.
    guestConfirmationRequestedAt: z.string().nullable().optional(),
    guestConfirmationRemindedAt: z.string().nullable().optional(),
    guestConfirmedAt: z.string().nullable().optional(),
    // ISO timestamp of when the booking was marked completed, or null.
    completedAt: z.string().nullable(),
    // ISO timestamp of when the guest tapped "Running late" on the hour-before
    // push, or null. Shown as a mark in the admin.
    runningLateAt: z.string().nullable().optional(),
    // Operator tags from RESERVATION_TAGS. `likely-no-show` forces commission
    // to 0 — see tagsSuppressCommission.
    tags: z.array(z.string()).default([]),
    // Women-only trips: when the team saw the deposit reach the organiser.
    depositReceivedAt: z.string().nullable().optional(),
    // Women-only trips: the departure city the guest picked, or null.
    tripDeparture: z.string().nullable().optional(),
    // Changes the guest made themselves from their account or the app, oldest
    // first. Non-empty == show "Changed by guest" in the admin.
    guestChanges: z.array(guestChangeEntrySchema).optional(),
    addons: z.array(reservationAddonDtoSchema).default([]),
    /**
     * Handle of the ambassador whose link brought the guest, or null. Only
     * the detail read joins it; list rows leave it out.
     */
    ambassadorHandle: z.string().nullable().optional(),
    /**
     * The ambassador's share as frozen into a payout (whole MAD), or null
     * while it is still derived from the commission.
     */
    ambassadorPaidMad: z.number().int().nullable().optional(),
  })
  .extend(timestampsSchema.shape);
export type ReservationDTO = z.infer<typeof reservationDtoSchema>;

/**
 * A money amount carried as a decimal string (MAD).
 *
 * Rejects blank and non-numeric input because every consumer eventually does
 * `Number(value)` on it: `Number("")` is 0 and `Number("1 560")` is NaN, so an
 * emptied or mistyped admin price field would silently persist as 0.00 rather
 * than failing the request.
 */
const moneyString = z
  .string()
  .refine((v) => v.trim() !== "" && Number.isFinite(Number(v)), {
    message: "expected a numeric amount",
  });

/**
 * The inline guest on a booking, where the phone number is optional.
 *
 * A walk-in or a booking relayed by a partner often arrives with a name and
 * nothing else, and refusing to record it just means the booking is never
 * logged at all. The clients CRUD keeps `whatsappNumber` required — this
 * relaxation is scoped to the booking path, where a contactless guest is a
 * real case rather than a half-filled form.
 */
const reservationClientSchema = clientUpsertSchema.extend({
  whatsappNumber: z.string().optional(),
});

const reservationCreateBaseObject = z.object({
  clientId: z.string().uuid().optional(),
  client: reservationClientSchema.optional(),

  activityId: z.string().uuid(),
  packageId: z.string().uuid().optional(),
  reservationDate: z.iso.date().optional(),
  reservationTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, "expected HH:mm")
    .optional(),

  comboId: z.string().uuid().optional(),
  couponId: z.string().uuid().optional(),
  reservationPrice: moneyString,
  commission: moneyString.default("0"),
  adults: z.number().int().min(1).default(1),
  children: z.number().int().min(0).default(0),
  infants: z.number().int().min(0).default(0),
  infantMaxAge: z.number().int().optional(),
  childrenMaxAge: z.number().int().optional(),
  transport: z.boolean().default(false),
  // Nullable as well as optional: omitting it means "leave as is", while an
  // explicit null clears the fee. Without null, switching pickup off leaves the
  // old amount sitting on a booking that no longer has pickup.
  transportPrice: moneyString.nullable().optional(),
  /** Optional (not defaulted) so existing callers keep compiling — the service
   *  coerces it to false and drops it unless the package prices a surcharge. */
  transportOutside: z.boolean().optional(),
  address: z.string().optional(),
  groups: z.number().int().min(0).default(0),
  rooms: z.number().int().min(0).default(0),
  hours: z.number().int().min(0).default(0),
  nights: z.number().int().min(0).default(0),
  lang: z.string().default("en"),
  paymentMethod: z.string().optional(),
  source: z.string().default("admin"),
  /**
   * Google Ads click id, sent by the web checkout when the visitor arrived from
   * an ad. Capped well above Google's real click-id length so a malformed or
   * hostile value can't bloat the row; anything longer is rejected outright
   * rather than silently truncated.
   */
  gclid: z.string().max(512).optional(),
  /**
   * First-touch channel captured on the landing page — and, on bookings the
   * team types in by hand, the channel they pick themselves ("this WhatsApp
   * booking came from a Google Ad"). Nullable as well as optional: omitting it
   * leaves whatever was recorded, an explicit null clears it back to unknown.
   */
  trafficSource: z.string().max(32).nullable().optional(),
  /** Which chat assistant sent an ai_assistant visit ("chatgpt", "perplexity", ...). */
  aiAssistant: z.string().max(32).nullable().optional(),
  bookingId: z.string().optional(),
  notes: z.string().optional(),
  addons: z.array(reservationAddonInputSchema).optional(),
  // Initial status. Defaults to "pending" so the customer-facing create path
  // (web checkout, mobile) keeps the "awaiting team confirmation" behavior.
  // Admin / MCP backfills (e.g. retroactive WhatsApp bookings) can pass
  // "confirmed" to skip the pending intermediate state.
  status: z.enum(["pending", "confirmed", "completed", "paid", "cancelled"]).optional(),
});

export const reservationCreateSchema = reservationCreateBaseObject.refine(
  (v) => Boolean(v.clientId) || Boolean(v.client),
  {
    message: "clientId or client required",
  },
);
export type ReservationCreateInput = z.infer<typeof reservationCreateSchema>;

export const reservationUpdateSchema = z
  .object(withoutDefaults(reservationCreateBaseObject.shape))
  .partial();
export type ReservationUpdateInput = z.infer<typeof reservationUpdateSchema>;

export const reservationReasonSchema = z.object({ reason: z.string().min(1) });
export type ReservationReasonInput = z.infer<typeof reservationReasonSchema>;

/**
 * Ready-made cancellation reasons. The client's email renders each one as a
 * full sentence in the client's own language; the row stores the English
 * label from RESERVATION_CANCEL_REASON_LABELS so the team can read it.
 */
export const RESERVATION_CANCEL_REASONS = ["no_answer", "fully_booked", "whatsapp_unreachable"] as const;
export type ReservationCancelReasonCode = (typeof RESERVATION_CANCEL_REASONS)[number];
export const RESERVATION_CANCEL_REASON_LABELS: Record<ReservationCancelReasonCode, string> = {
  no_answer: "No answer",
  fully_booked: "Fully booked",
  whatsapp_unreachable: "WhatsApp number not reachable",
};

/** Cancel: every field optional. `silent` cancels without emailing the client. */
export const reservationCancelSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
  reasonCode: z.enum(RESERVATION_CANCEL_REASONS).optional(),
  silent: z.boolean().optional(),
});
export type ReservationCancelInput = z.infer<typeof reservationCancelSchema>;

export const reservationAcceptSchema = z.object({ reason: z.string().min(1).optional() });
export type ReservationAcceptInput = z.infer<typeof reservationAcceptSchema>;

/** Channels the team can mark as "we've reached the client". */
export const RESERVATION_CONTACT_CHANNELS = ["whatsapp", "email"] as const;
export type ReservationContactChannel = (typeof RESERVATION_CONTACT_CHANNELS)[number];

/** Toggle a contact-channel flag on a reservation (manual ops outreach tracking). */
export const reservationContactStatusSchema = z.object({
  channel: z.enum(RESERVATION_CONTACT_CHANNELS),
  contacted: z.boolean(),
});
export type ReservationContactStatusInput = z.infer<typeof reservationContactStatusSchema>;

/** A free-text email the team writes to the client from the admin menu. */
export const reservationMessageSchema = z.object({
  subject: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(5000),
});
export type ReservationMessageInput = z.infer<typeof reservationMessageSchema>;

/**
 * Replace the operator tags on a reservation.
 *
 * Whole-set PUT rather than add/remove calls: the admin renders a checklist, so
 * the client already knows the full intended set, and a replace cannot leave
 * two operators toggling the same row into a state neither of them chose.
 */
export const reservationTagsSchema = z.object({
  tags: z.array(z.enum(RESERVATION_TAG_IDS as [string, ...string[]])).max(RESERVATION_TAG_IDS.length),
});
export type ReservationTagsInput = z.infer<typeof reservationTagsSchema>;

export const reservationsListQuerySchema = listQueryBaseSchema.extend({
  status: z.coerce.number().int().optional(),
  source: z.string().optional(),
  /**
   * Filter by Google Ads attribution. `true` = only bookings that carry a
   * gclid, `false` = only those that don't. Omitted = no filtering.
   * Separate from `source`, which is the channel/site the booking came through
   * — an ad-driven booking is still `source: "web"`.
   */
  fromGoogleAds: queryBoolean(),
  transport: queryBoolean(),
  clientId: z.string().uuid().optional(),
  comboId: z.string().uuid().optional(),
  /**
   * Only reservations booked on these activities (matched via
   * activity_reservations). Comma-separated so the admin can watch several at
   * once — "quad Casablanca and paragliding Rabat" is one question, not two.
   */
  /** Only bookings that first arrived through this channel (see TRAFFIC_SOURCES). */
  trafficSource: z.string().max(32).optional(),
  /** Only bookings whose first touch carried this utm_campaign (Google Ads campaign id). */
  adCampaign: z.string().max(64).optional(),
  activityId: z
    .string()
    .optional()
    .refine((v) => v === undefined || parseUuidList(v).length > 0, {
      message: "expected one or more uuids",
    }),
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  reservationFrom: z.iso.date().optional(),
  reservationTo: z.iso.date().optional(),
});
export type ReservationsListQuery = z.infer<typeof reservationsListQuerySchema>;
